import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CreditCard, User, Settings, Plus, Copy, ExternalLink } from 'lucide-react';

// Get backend URL - use env var or same origin
const getBackendUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (envUrl) return envUrl;
  return window.location.origin;
};

const BACKEND_URL = getBackendUrl();

// Helper to get auth headers
const getAuthConfig = () => {
  const token = localStorage.getItem('session_token');
  return {
    withCredentials: true,
    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
  };
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [cardTitle, setCardTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [cardCredits, setCardCredits] = useState(0);
  const [pricingPackages, setPricingPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    fetchData();
    // Check for payment callback
    const orderNumber = searchParams.get('order');
    if (orderNumber) {
      verifyPayment(orderNumber);
    }
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const authConfig = getAuthConfig();
      const [profileRes, cardsRes, pricingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/profile`, authConfig),
        axios.get(`${BACKEND_URL}/api/cards`, authConfig),
        axios.get(`${BACKEND_URL}/api/pricing`, authConfig)
      ]);
      setUser(profileRes.data);
      setCardCredits(profileRes.data.card_credits || 0);
      setCards(cardsRes.data);
      setPricingPackages(pricingRes.data.packages || []);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('session_token');
        navigate('/login');
        return;
      }
      toast.error('Gagal memuatkan data');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (orderNumber) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/verify/${orderNumber}`,
        getAuthConfig()
      );
      if (response.data.status === 'success') {
        toast.success(`Pembayaran berjaya! ${response.data.credits_added} kad telah ditambah.`);
        // Remove order param from URL
        window.history.replaceState({}, '', '/dashboard');
        fetchData(); // Refresh data
      }
    } catch (error) {
      toast.error('Gagal mengesahkan pembayaran');
    }
  };

  const isAdmin = user?.role === 'admin';

  const handleCreateCardClick = () => {
    if (!isAdmin && cardCredits <= 0) {
      // Show payment dialog instead
      setPaymentDialogOpen(true);
    } else {
      // Navigate to template selection page
      navigate('/templates');
    }
  };

  const handleCreateCard = async () => {
    if (!cardTitle.trim()) {
      toast.error('Sila masukkan tajuk kad');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/cards/quick`,
        { title: cardTitle.trim() },
        getAuthConfig()
      );
      toast.success('Kad berjaya dicipta!');
      setCreateDialogOpen(false);
      setCardTitle('');
      navigate(`/editor/${response.data.card_id}`);
    } catch (error) {
      if (error.response?.status === 402) {
        // No credits - show payment dialog
        setCreateDialogOpen(false);
        setPaymentDialogOpen(true);
        toast.error('Tiada baki kad. Sila buat pembayaran.');
      } else {
        toast.error(error.response?.data?.detail || 'Gagal mencipta kad');
      }
    } finally {
      setCreating(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedPackage) {
      toast.error('Sila pilih pakej');
      return;
    }

    // Check if user has WhatsApp number
    if (!user?.whatsapp) {
      toast.error('Sila kemaskini no. WhatsApp anda di Profil sebelum membuat pembayaran.', { duration: 5000 });
      setPaymentDialogOpen(false);
      navigate('/profile');
      return;
    }

    setProcessingPayment(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/create`,
        { quantity: selectedPackage.quantity },
        getAuthConfig()
      );
      
      if (response.data.payment_url) {
        // Redirect to Bayarcash payment page
        window.location.href = response.data.payment_url;
      } else {
        toast.error('Gagal mendapatkan pautan pembayaran');
      }
    } catch (error) {
      if (error.response?.status === 422) {
        toast.error('Sila kemaskini no. WhatsApp anda di Profil sebelum membuat pembayaran.', { duration: 5000 });
        setPaymentDialogOpen(false);
        navigate('/profile');
      } else {
        toast.error(error.response?.data?.detail || 'Gagal memproses pembayaran');
      }
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, getAuthConfig());
      localStorage.removeItem('session_token');
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D348B] mx-auto mb-4"></div>
          <p className="text-gray-600">Memuatkan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Dashboard | Kawkad</title>
      </Helmet>
      <nav className="bg-[#3D348B] sticky top-0 z-40 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-white">Kawkad</h1>
          <div className="flex items-center gap-4">
            {user?.role === 'admin' && (
              <Button
                data-testid="nav-admin-btn"
                variant="outline"
                onClick={() => navigate('/admin')}
                className="border-[#F7B801] text-[#F7B801] hover:bg-[#F7B801] hover:text-[#3D348B] rounded-full"
              >
                Panel Admin
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={() => navigate('/profile')}
              className="text-white/80 hover:bg-white/10 hover:text-white flex items-center gap-2"
            >
              <User className="w-4 h-4" />
              {user?.name}
            </Button>
            <Button
              data-testid="nav-logout-btn"
              variant="ghost"
              onClick={handleLogout}
              className="text-white/80 hover:bg-white/10 hover:text-white"
            >
              Log Keluar
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold text-[#3D348B]">Kad Saya</h2>
            <p className="text-gray-600 mt-1">Urus dan edit kad jemputan anda</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* Card Credits Display */}
            <div className="flex items-center gap-2 text-sm">
              <CreditCard className="w-4 h-4 text-[#7678ED]" />
              <span className="text-gray-600">Baki kad boleh dicipta:</span>
              {isAdmin ? (
                <span className="font-bold text-[#F18701]">∞ Unlimited</span>
              ) : (
                <span className={`font-bold ${cardCredits > 0 ? 'text-[#3D348B]' : 'text-[#F35B04]'}`}>
                  {cardCredits}
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {!isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => setPaymentDialogOpen(true)}
                  className="border-[#7678ED] text-[#7678ED] hover:bg-[#7678ED] hover:text-white rounded-full px-6"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Tambah Token Kad
                </Button>
              )}
              <Button
                data-testid="create-card-btn"
                onClick={handleCreateCardClick}
                className="bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] rounded-full px-8 font-semibold"
              >
                Cipta Kad Baharu
              </Button>
            </div>
          </div>
        </div>

        {cards.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-cards-message">
            <p className="text-gray-600 text-lg mb-4">Anda belum mempunyai sebarang kad.</p>
            <p className="text-gray-500">Klik butang "Cipta Kad Baharu" untuk bermula.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((card) => {
              const shareUrl = `${window.location.origin}/${card.slug}`;
              const ogShareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/og/${card.slug}`;
              
              const copyShareLink = (e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(ogShareUrl);
                toast.success('Link telah disalin!');
              };
              
              const openShareLink = (e) => {
                e.stopPropagation();
                window.open(shareUrl, '_blank');
              };
              
              return (
                <div
                  key={card.card_id}
                  data-testid={`card-${card.card_id}`}
                  className="bg-white rounded-xl overflow-hidden card-shadow hover-lift cursor-pointer group"
                  onClick={() => navigate(`/editor/${card.card_id}`)}
                >
                  {/* Thumbnail - Use background_image directly */}
                  <div className="aspect-video bg-gradient-to-br from-[#7678ED]/10 to-[#3D348B]/10 relative overflow-hidden">
                    {card.background_image ? (
                      <img 
                        src={card.background_image} 
                        alt="Card background" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <span className="text-sm">Tiada gambar</span>
                      </div>
                    )}
                    
                    {/* Hover Overlay - Only for published cards */}
                    {card.status === 'published' && (
                      <div className="absolute inset-0 bg-[#3D348B]/80 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-3">
                        <p className="text-white text-xs mb-1">Link Kongsi:</p>
                        <div className="bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 flex items-center gap-2 max-w-[90%]">
                          <span className="text-white text-xs truncate">{ogShareUrl}</span>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={copyShareLink}
                            className="text-xs h-8 bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701]"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Salin
                          </Button>
                          <Button
                            size="sm"
                            onClick={openShareLink}
                            className="text-xs h-8 bg-white text-[#3D348B] hover:bg-gray-100"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Buka
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    {card.title && (
                      <p className="text-sm text-gray-500 mb-1">{card.title}</p>
                    )}
                    <h3 className="font-semibold text-xl text-[#3D348B] mb-2">
                      {card.bride_name} & {card.groom_name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">{card.wedding_date}</p>
                    <div className="flex items-center justify-between">
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        card.status === 'published' ? 'bg-[#7678ED]/10 text-[#3D348B]' : 'bg-[#F7B801]/20 text-[#F18701]'
                      }`}>
                        {card.status === 'published' ? 'Diterbitkan' : 'Draf'}
                      </span>
                      <span className="text-sm text-gray-500">/{card.slug}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Card Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#3D348B]">Cipta Kad Baharu</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="card-title">Tajuk Kad</Label>
              <Input
                id="card-title"
                data-testid="card-title-input"
                value={cardTitle}
                onChange={(e) => setCardTitle(e.target.value)}
                placeholder="contoh: Majlis Perkahwinan Ali & Fatimah"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateCard();
                  }
                }}
              />
              <p className="text-xs text-gray-500">Tajuk ini untuk rujukan anda sahaja</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              data-testid="create-card-submit-btn"
              onClick={handleCreateCard}
              disabled={creating || !cardTitle.trim()}
              className="w-full bg-[#3D348B] text-white hover:bg-[#7678ED] rounded-full"
            >
              {creating ? 'Mencipta...' : 'Cipta Kad'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#3D348B]">Beli Kad Jemputan</DialogTitle>
            <DialogDescription>
              Pilih pakej yang bersesuaian untuk keperluan anda
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {pricingPackages.map((pkg, idx) => (
              <div
                key={idx}
                onClick={() => setSelectedPackage(pkg)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  selectedPackage?.quantity === pkg.quantity 
                    ? 'border-[#7678ED] bg-[#7678ED]/10' 
                    : 'border-gray-200 hover:border-[#7678ED]'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-semibold text-lg">{pkg.label}</p>
                    <p className="text-sm text-gray-500">
                      {pkg.quantity > 1 ? `RM${(pkg.price / pkg.quantity).toFixed(0)}/kad` : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-[#3D348B]">RM{pkg.price}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              onClick={handlePayment}
              disabled={!selectedPackage || processingPayment}
              className="w-full bg-[#F7B801] text-[#3D348B] hover:bg-[#F18701] rounded-full py-6 font-semibold"
            >
              {processingPayment ? 'Memproses...' : `Bayar RM${selectedPackage?.price || '0'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
